import {
  Config,
  getGasPrice,
  prepareTransactionRequest,
  Transport,
} from '@wagmi/core';
import { useGasPrice } from 'wagmi';
import { Chain, Hex, HttpTransport, parseUnits } from 'viem';
import { useQuery } from '@tanstack/react-query';
import { BTCNetworkAddressType, BTCNetworkType } from '../api/type';
import { btcDecimals, mockBtcEvmChainId, sendTx } from '../config/btc';
import {
  getConnection,
  getSendSplToken,
  mockSolEvmChainId,
  sendSolTx,
  ToSerializeTransaction,
} from '../config/sol';
import { Web3Type } from '../config/type';
import { IChainId, ITransactionToken, ITransactionType } from '../state/type';
import { setPassKey } from '../util';
import {
  btcSignPsbtAndPush,
  sendEvmTransactionApi,
  solSignRawTransaction,
  tonSignMessage,
} from '../api';
import useTransactions from './useTransactions';
import useTomoUserInfo from './useTomoUserInfo';
import { MFAType, useMFAVerify } from './useMFAVerify';
import {
  createSigningTransaction,
  mockTonChainId,
  sendTransaction,
  TonSigningTransactionType,
} from '../config/ton';
import useChains from './useChains';

export const useReadyBtcTransaction = (params: {
  network: BTCNetworkType;
  addressType: BTCNetworkAddressType | undefined;
  toAddress: string | undefined;
  amount: string;
}) => {
  const { btcAddress } = useTomoUserInfo();

  const readyBtcTransaction = useQuery({
    queryKey: [
      'readyBtcTransaction',
      params.addressType,
      params.amount,
      params.network,
      params.toAddress,
      btcAddress,
    ],
    queryFn: async () => {
      const txStr = await sendTx({
        network: params.network, // my Address
        addressType: params.addressType, // toAddress
        toAddress: params.toAddress, //value
        amount: params.amount, // contract Address  // token address or undefined
      });
      return txStr;
    },
  });

  return readyBtcTransaction;
};

const useSendTransaction = () => {
  const { setTransactions } = useTransactions();
  const { getMFAParams } = useMFAVerify();
  const connect = getConnection();
  const { getChain } = useChains();

  const sendEVMTransaction = async (params: {
    chainId: IChainId | undefined;
    fromAddress: string | undefined;
    toAddress: string | undefined;
    value: bigint | undefined;
    rpc?: string;
    config: Config;
    data?: string | undefined;
    tokenValue?: bigint | undefined;
    token?: ITransactionToken | undefined;
    mfaType?: MFAType | undefined;
    password?: string | undefined;
    historyType?: ITransactionType['historyType'];
  }) => {
    try {
      console.log('sendEVMTransaction', params);
      const data = params.data || '0x';
      const chainId = params.chainId;
      const chain = getChain(chainId);

      const gasPrice = await getGasPrice(params.config, {
        chainId: chainId,
      });

      // @ts-ignore
      const res = await prepareTransactionRequest(params.config, {
        chainId: chainId,
        account: params.fromAddress as Hex,
        to: params.toAddress as Hex,
        value: params.value || BigInt(0),
        data: data as Hex,
      });

      if (res) {
        const mfaParams = {
          gas: '0x' + res.gas.toString(16),
          gasPrice: '0x' + gasPrice.toString(16),
          value: '0x' + res.value?.toString(16),
          from: res.from,
          to: res.to,
          nonce: res.nonce,
          data: data,
        };
        if (!chainId) return;
        const mfaRes = await getMFAParams(
          {
            data: mfaParams,
            chainid: chainId,
          },
          params.mfaType,
          params.password
        );
        if (mfaRes) {
          const { mfa } = mfaRes;
          if (!mfa) {
            return;
          }
          setPassKey(mfa);
          const rpc = params.rpc || chain?.chain?.rpcUrls?.default?.http[0];
          if (!rpc) {
            return;
          }
          const tranRes = await sendEvmTransactionApi(mfa, {
            transaction: mfaParams,
            chainId,
            rpc: rpc,
          });
          if (tranRes && tranRes.result) {
            const historySave: ITransactionType = {
              fromAddress: params?.fromAddress,
              toAddress: params?.toAddress,
              fromAmount: params?.tokenValue?.toString(),
              toAmount: params?.tokenValue?.toString() || '0',
              fromSwapTokens: params.token,
              toSwapTokens: params.token,
              nonce: res.nonce,
              time: new Date().getTime(),
              hash: tranRes.result,
              historyType: params.historyType ? params.historyType : 'Send',
              chainId: chainId,
            };

            setTransactions({
              transaction: historySave,
            });

            return tranRes;
          }
        }
      } else {
        console.error('prepareTransactionRequest');
      }
    } catch (error) {
      console.warn({
        error,
      });
      if (error.name === 'EstimateGasExecutionError') {
        console.error(error.details);
      } else {
        console.error(error.details ? error.details : error);
      }
    }
  };

  const sendSolTransaction = async (params: {
    fromAddress: string | undefined;
    toAddress: string | undefined;
    value: bigint | undefined; //1e9   decimal: 9
    contract?: string;
    token?: ITransactionToken | undefined;
    data?: string | undefined;
    mfaType?: MFAType | undefined;
    password?: string | undefined;
    historyType?: ITransactionType['historyType'];
  }) => {
    try {
      if (params.value && params.fromAddress) {
        let txStr;
        if (params.data) {
          txStr = await ToSerializeTransaction(params.data);
        } else {
          if (!params.contract) {
            txStr = await sendSolTx(
              params.fromAddress, // my Address
              params.toAddress, // toAddress
              params.value || BigInt(0), //value
              // signData.txMeta.mintAddress // contract Address
              params.contract
            );
          } else {
            txStr = await getSendSplToken(
              params.contract,
              params.fromAddress,
              params.toAddress,
              params.value
            );
          }
        }

        const mfaRes = await getMFAParams(
          {
            data: txStr,
            chainid: mockSolEvmChainId,
          },
          params.mfaType,
          params.password
        );

        if (mfaRes.mfa) {
          setPassKey(mfaRes.mfa);

          const signRes = await solSignRawTransaction({
            rawTransaction: txStr ?? '',
          });

          if (signRes) {
            const hash = await connect.sendRawTransaction(
              Buffer.from(signRes.result, 'hex')
            );

            if (!hash) {
              return;
            }

            const historySave: ITransactionType = {
              fromAddress: params?.fromAddress,
              toAddress: params?.toAddress,
              fromAmount: params?.value?.toString(),
              toAmount: params?.value?.toString() || '0',
              fromSwapTokens: params.token,
              toSwapTokens: params.token,
              nonce: new Date().getTime(),
              time: new Date().getTime(),
              hash: hash,
              historyType: params.historyType ? params.historyType : 'Send',
              chainId: mockSolEvmChainId,
            };

            setTransactions({
              transaction: historySave,
            });

            if (hash) {
              return hash;
            }
          }
        }
      }
    } catch (error) {
      console.error({
        error,
      });
    }
  };

  /*
  const sendSwapSolTransaction = async (params: {
    swapInfoData?: any;
    token?: ITransactionToken | undefined;
    mfaType?: MFAType | undefined;
    password?: string | undefined;
    historyType?:  ITransactionType['historyType'];
  }) => {
    const connect = getConnection();

    const transaction = await ToSerializeTransaction(params.swapInfoData);

    const mfaRes = await getMFAParams(
      {
        data: transaction,
        chainid: mockSolEvmChainId,
      },
      params.mfaType,
      params.password
    );

    if (mfaRes.mfa) {
      setPassKey(mfaRes.mfa);
      const signRes = await solSignRawTransaction({
        rawTransaction: transaction ?? '',
      });

      if (signRes) {
        const transaction = Buffer.from(signRes.result, 'hex');

        const sendTransaction = async () => {
          let hash;
          try {
            for (let i = 0; i < 30; i++) {
              hash = await connect.sendRawTransaction(transaction);
              await sleep(1_000);
              const result = await connect.getSignatureStatus(hash);
            }
          } catch (error) {
            if (!hash) {
              return;
            }
            const historySave: IHistoryType = {
              fromAddress,
              toAddress,
              fromAmount: parseUnits(
                inputCount,
                fromToken?.decimals || 18
              ).toString(),
              toAmount: outputCounts.value,
              fromSwapTokens: {
                token: {
                  chain_id: Number(fromToken?.chainId),
                  id: Number(fromToken?.id),
                  image: fromToken?.imageUrl || '',
                  market_cap: fromToken?.marketCap || 0,
                  name: fromToken?.name || '',
                  price: Number(fromToken?.priceUsd),
                  price_change_h24: fromToken?.priceChangeH24 || 0,
                  symbol: fromToken?.symbol || '',
                  contract: fromToken?.address,
                  balance: '0',
                  formatted: '0',
                  decimals: fromToken?.decimals || 18,
                  balanceItem: undefined,
                },
                chain: fromNetwork,
                balance: undefined,
              },
              toSwapTokens: {
                token: {
                  chain_id: Number(toToken?.chainId),
                  id: Number(toToken?.id),
                  image: toToken?.imageUrl || '',
                  market_cap: toToken?.marketCap || 0,
                  name: toToken?.name || '',
                  price: Number(toToken?.priceUsd),
                  price_change_h24: toToken?.priceChangeH24 || 0,
                  symbol: toToken?.symbol || '',
                  contract: toToken?.address,
                  balance: '0',
                  formatted: '0',
                  decimals: toToken?.decimals || 18,
                  balanceItem: undefined,
                },
                chain: toNetwork,
                balance: undefined,
              },
              type: 'Rango',
              nonce: new Date().getTime(),
              time: new Date().getTime(),
              hash: hash,
              chain: fromNetwork,
              historyType: 'Swap',
            };

            console.log({
              historySave,
            });

            const items = swapHistorys[mockSolEvmChainId];
            const hisotrys = [historySave, ...(items ? items : [])];
            setSwapHistorys({
              ...swapHistorys,
              [mockSolEvmChainId]: hisotrys.splice(0, 5),
            });

            // saveSwapSPLTokens, setSaveSwapSPLTokens

            if (saveSwapSPLTokens) {
              if (toToken && toToken?.address) {
                const lastItem = saveSwapSPLTokens.find(item => {
                  return (
                    item.token.address === toToken?.address &&
                    item.chainId === mockSolEvmChainId
                  );
                });
                if (!lastItem) {
                  setSaveSwapSPLTokens([
                    ...saveSwapSPLTokens,
                    {
                      network: toNetwork,
                      token: toToken,
                      chainId: mockSolEvmChainId,
                    },
                  ]);
                }
              }
            } else {
              if (toToken && toToken?.address) {
                setSaveSwapSPLTokens([
                  {
                    network: toNetwork,
                    token: toToken,
                    chainId: mockSolEvmChainId,
                  },
                ]);
              }
            }

            if (hash) {
              toast.success(
                'Sign & Send successful! Redirecting to the previous page in 3 seconds.'
              );
              setTimeout(() => {
                params.setShowConfirm(false);
                throw { success: true, error };
              }, 3000);
            }

            return hash;
          }
        };
        await sendTransaction();
      }
    }
  };
*/

  const sendBtcTransaction = async (params: {
    network: BTCNetworkType;
    addressType: BTCNetworkAddressType;
    value: string;
    toAddress: string | undefined;
    token?: ITransactionToken;
    mfaType?: MFAType | undefined;
    password?: string | undefined;
    historyType?: ITransactionType['historyType'];
  }) => {
    const amount = parseUnits(params.value, btcDecimals).toString();

    const txStr = await sendTx({
      network: params.network, // my Address
      addressType: params.addressType, // toAddress
      toAddress: params.toAddress, //value
      amount: amount,
    });

    const mfaRes = await getMFAParams(
      {
        data: txStr,
        chainid: mockSolEvmChainId,
      },
      params.mfaType,
      params.password
    );

    if (mfaRes) {
      setPassKey(mfaRes.mfa);

      const res = await btcSignPsbtAndPush({
        networkType: params.network,
        addressType: params.addressType, // toAddress
        psbtHex: txStr.result.psbtHex,
        autoFinalized: true,
      });

      if (res && res.result) {
        // if (tranRes.code !== 0) {
        //   throw new Error(tranRes.message)
        // }
        const hash = res.result;

        const historySave: ITransactionType = {
          fromAddress: params.network,
          toAddress: params.addressType,
          fromAmount: amount,
          toAmount: amount,
          fromSwapTokens: params.token,
          toSwapTokens: params.token,
          nonce: new Date().getTime(),
          time: new Date().getTime(),
          hash: hash,
          historyType: params.historyType ? params.historyType : 'Send',
          chainId: mockBtcEvmChainId,
        };

        setTransactions({
          transaction: historySave,
        });

        return hash;
      }
    }
  };

  const sendTonTransaction = async (
    params: TonSigningTransactionType & {
      mfaType?: MFAType | undefined;
      password?: string | undefined;
      token?: ITransactionToken;
      historyType?: ITransactionType['historyType'];
    }
  ) => {
    try {
      const singingMessage = await createSigningTransaction(params);
      // console.log('signingMessageBoc', singingMessage);
      if (!singingMessage?.signingMessageBoc) return;

      const mfaRes = await getMFAParams(
        {
          data: singingMessage,
          chainid: mockTonChainId,
        },
        params.mfaType,
        params.password
      );

      const { mfa } = mfaRes;
      // console.log('mfa', mfa);
      if (!mfa) {
        return;
      }
      setPassKey(mfa);

      const mfaParams = {
        signingMessageBoc: singingMessage.signingMessageBoc,
        stateInitBoc: singingMessage?.stateInitBoc || '',
      };
      const { result: signedTransaction, code, message } = await tonSignMessage(
        mfa,
        mfaParams
      );
      // console.log('mfa', signedTransaction, code, message);
      if (code != 10000) return message;

      const tranRes = await sendTransaction(signedTransaction);
      // const tranRes = {'@type': 'ok', '@extra': '1724056253.9801953:10:0.38478557394306645', 'msgHash': ''};
      const amount = `${Number(params?.value) * 1e9}`;
      if (tranRes && tranRes['@type'] == 'ok') {
        const historySave: ITransactionType = {
          fromAddress: params.fromAddress,
          toAddress: params.toAddress,
          fromAmount: amount,
          toAmount: amount,
          fromSwapTokens: params.token,
          toSwapTokens: params.token,
          nonce: new Date().getTime(),
          time: new Date().getTime(),
          hash: tranRes?.msgHash || '',
          historyType: params.historyType ? params.historyType : 'Send',
          chainId: mockTonChainId,
        };

        setTransactions({
          transaction: historySave,
        });

        return tranRes?.msgHash || '';
      }
    } catch (error) {
      console.warn({
        error,
      });
      if (error.name === 'EstimateGasExecutionError') {
        console.error(error.details);
      } else {
        console.error(error.details ? error.details : error);
      }
    }
  };

  return {
    sendEVMTransaction,
    sendSolTransaction,
    sendTonTransaction,
    sendBtcTransaction,
  };
};

export default useSendTransaction;
